import { FC, Fragment, useEffect, useMemo, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-regular-svg-icons";
import {
  faFileDownload,
  faFileUpload,
  faFilter,
  faCloudArrowDown,
  faLoader,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate, useParams } from "react-router-dom";
import { PlusIcon } from "@heroicons/react/20/solid";
import { useTranslation } from "react-i18next";
import { Formik, useFormik } from "formik";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import * as Yup from "yup";

import {
  paymentMethodOptions,
  transactionTypeOptions,
} from "@/app/accounting/accounting";
import {
  useAccountingsQuery,
  useAgreementsQuery,
  useAgreementsSummaryQuery,
  useCurrenciesQuery,
  useLazyExportAccountingQuery,
  useLazyExportSalesAnalysisQuery,
  useImportXmlMutation,
  useLazyExportXmlQuery,
  useLazyExportDebitQuery,
  useLazyExportHistoryQuery,
  useFailedHistoryQuery,
  useCreateCreditorSepaMutation,
} from "@/app/accounting/accounting.api";

import {
  useBranchsGymQuery,
  usePersonalQuery,
} from "@/app/branchs/branchs.api";
import { currencies } from "@/app/fees/fees";
import {
  useCreateTransactionMutation,
  useLazyExportTransactionsQuery,
  useTransactionsQuery,
  useLazyTransactionTicketQuery,
  useDeleteTransactionMutation,
} from "@/app/transactions/transactions.api";
import { Button } from "@/shared/components/atoms/Button";
import { InputDropDownSpan } from "@/shared/components/atoms/InputDropDownSpan";
import { InputWithCornerHint } from "@/shared/components/atoms/InputWithCornerHint";
import { AccountingFilter } from "@/shared/components/molecules/AccountingFilter";
import { AgreementsTable } from "@/shared/components/organisms/AgreementsTable";
import { AgreementsSummaryTable } from "@/shared/components/organisms/AgreementsSummaryTable";
import { HistoryTable } from "@/shared/components/organisms/HistoryTable";
import { DialogCreate } from "@/shared/components/molecules/DialogCreate";
import { AccountingTable } from "@/shared/components/organisms/AccountingTable";
import { ButtonSecondary } from "@/shared/components/atoms/ButtonSecondary";

import { Select } from "@/shared/components/organisms/Select";
import { TransactionsTable } from "@/shared/components/organisms/TransactionsTable";
import {
  transactionInitialValues,
  transactionValidationSchema,
  xmlInitialValues,
  xmlValidationSchema,
} from "@/shared/schemas/accounting.schema";
import { toasts } from "@/shared/utils/toasts";

import { DateRangePicker } from "@/shared/components/atoms/DataRangePicker";
import { ModalXXL } from "@/shared/components/organisms/ModalXXL";
import { useMutationToast } from "@/shared/hooks/useMutationToast";
import { Modal } from "@/shared/components/organisms/Modal";
import { InputFileXML } from "@/shared/components/atoms/InputFileXML";
import { selectBranch } from "@/app/branchs/branch.slice";

import {
  xmlImportInitialValues,
  xmlImportValidationSchema,
  XMLImportValues,
} from "@/shared/schemas/accounting.schema";
import { selectUser } from "@/app/auth/auth.slice";
import { Permission } from "@/shared/hooks/usePermission";
import { Loader2Icon, TriangleAlert } from "lucide-react";
import AlertErrorButtons from "@/shared/components/molecules/AlertErrorButtons";
import { useDebounce } from "@/shared/hooks/debounce";
import { DOMAIN } from "@/app/app.api";

export const AccountingPage: FC<any> = () => {
  const { id } = useParams();
  const [t] = useTranslation();
  const navigate = useNavigate();
  const user = useSelector(selectUser);

  const branch = useSelector(selectBranch);
  const [currentFee, setCurrentFee] = useState<undefined>();
  const [currentTransactionId, setCurrentTransactionId] = useState(null);

  //deleteTransaction
  const [idTicket, setIdTicket] = useState<number | undefined>(0);
  const [filterVisible, setFilterVisible] = useState(false);
  const [transactionFormVisible, setTransactionFormVisible] = useState(false);
  const [agreementsVisible, setAgreementsVisible] = useState(false);
  const [agreementsSummaryVisible, setAgreementsSummaryVisible] =
    useState(false);
  const [confirmSummaryVisible, setConfirmSummaryVisible] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [manualDirectDebitVisible, setManualDirectDebitVisible] =
    useState(false);
  const [openModalImportXML, setOpenModalImportXML] = useState(false);

  const [page, setPage] = useState(1);
  const [isEnd, setIsEnd] = useState(false);

  const branchsQuery = useBranchsGymQuery({ branchId: id });
  const personalQuery = usePersonalQuery({ branchId: id, page });
  const currenciesQuery = useCurrenciesQuery({
    branchId: id,
  });

  const branchsOptions = useMemo(() => {
    if (user?.role && user?.permissions) {
      const canAccessMultipleBranches = user?.permissions.includes(
        Permission.MULTI_CENTRO
      );

      if (canAccessMultipleBranches) {
        return (
          branchsQuery?.data
            ?.filter((branch) => branch.id !== user?.branchId)
            .map((branch) => ({
              id: branch.id,
              name: branch.branch,
              value: branch.id,
            })) ?? []
        );
      }

      return (
        branchsQuery?.data
          ?.filter((branch) => branch.id !== user?.branch?.id)
          .map((branch) => ({
            id: branch.id,
            name: branch.branch,
            value: branch.id,
          })) ?? []
      );
    }
  }, [branchsQuery, user?.role, user?.permissions]);

  const currencyOptions = useMemo(
    () =>
      currenciesQuery?.data?.map((currency, index) => ({
        id: index + 1,
        name: currency,
        value: currency,
      })) ?? [],
    [currenciesQuery]
  );

  const personalOptions = useMemo(
    () =>
      personalQuery?.data?.data?.data?.map((personal) => ({
        id: personal.id,
        name: personal.name,
      })),
    [personalQuery]
  );

  const filterFormik = useFormik({
    initialValues: {
      currency: "",
      startDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
      maxAmount: 999999,
      minAmount: 0,
      branchIds: [id],
      personalIds: [],
    },
    onSubmit: () => {},
  });

  const agreementsFormik = useFormik({
    initialValues: {
      startDate: DateTime.now().minus({ days: 1 }).toFormat("yyyy-MM-dd"),
      endDate: DateTime.now().toFormat("yyyy-MM-dd"),
    },
    onSubmit: () => {
      console.log("SE ENVIA EL FORMULARIO");
    },
  });

  const agreementsQuery = useAgreementsQuery({
    branchId: id,
    startDate: agreementsFormik.values.startDate,
    endDate: agreementsFormik.values.endDate,
  });

  console.log({ agreementsQuery });

  const agreementsSummaryQuery = useAgreementsSummaryQuery({
    branchId: id,
    startDate: agreementsFormik.values.startDate,
    endDate: agreementsFormik.values.endDate,
    tag: idTicket,
  });

  const [allAccountings, setAllAccountings] = useState<any[]>([]);

  const {
    data: accountings,
    isLoading,
    isFetching,
    isUninitialized,
    refetch,
  } = useAccountingsQuery({
    branchId: id,
    minAmount: filterFormik.values.minAmount,
    maxAmount: filterFormik.values.maxAmount,
    branchIds: filterFormik.values.branchIds,
    personalIds: filterFormik.values.personalIds,
    startDate: filterFormik.values.startDate,
    endDate: filterFormik.values.endDate,
    currency:
      typeof filterFormik.values.currency === "string"
        ? filterFormik.values.currency
        : filterFormik.values.currency.name,
    page,
  });

  const {
    data: transactions,
    isLoading: isLoadingTransactions,
    isFetching: isFetchingTransactions,
    refetch: refetchTransactions,
  } = useTransactionsQuery({
    branchId: id,
    minAmount: filterFormik.values.minAmount,
    maxAmount: filterFormik.values.maxAmount,
    branchIds: filterFormik.values.branchIds,
    personalIds: filterFormik.values.personalIds,
    startDate: filterFormik.values.startDate,
    endDate: filterFormik.values.endDate,
    currency:
      typeof filterFormik.values.currency === "string"
        ? filterFormik.values.currency
        : filterFormik.values.currency.name,
  });

  const failedHistoryQuery = useFailedHistoryQuery({
    branchId: filterFormik.values.branchIds,
  });

  useEffect(() => {
    if (accountings) {
      console.log({ isUninitialized });
      refetch();
      console.log("Coincide con la data de accountings I");
    }
    if (transactions) {
      refetchTransactions();
    }
  }, [filterFormik.values]);

  useEffect(() => {
    if (!accountings) return;

    setAllAccountings((prevAccountings) => [
      ...prevAccountings,
      ...accountings,
    ]);

    if (accountings.length === 0) {
      setIsEnd(true);
    } else {
      setIsEnd(false);
    }
  }, [accountings]);

  useEffect(() => {
    if (currencyOptions[0]) {
      filterFormik.setFieldValue("currency", currencyOptions[0]);
    }
  }, [currencyOptions]);

  useEffect(() => {
    setAllAccountings([]);
    setPage(1);
    setIsEnd(false);
  }, [filterFormik.values]);

  const [exportAccounting, exportAccountingResult] =
    useLazyExportAccountingQuery();
  const [exportTransactions, exportTransactiosResult] =
    useLazyExportTransactionsQuery();
  const [exportSalesAnalysis, exportSalesAnalysisResult] =
    useLazyExportSalesAnalysisQuery();
  const [transactionTicket, transactionTicketResult] =
    useLazyTransactionTicketQuery();
  const [createTransaction, createTransactionResult] =
    useCreateTransactionMutation();
  const [deleteTransaction, deleteTransactionResult] =
    useDeleteTransactionMutation();

  const [createDebit, createDebitResult] = useLazyExportDebitQuery();

  const [exportHistory, exportHistoryResult] = useLazyExportHistoryQuery();

  const [createCreditorSepa, createCreditorSepaResult] =
    useCreateCreditorSepaMutation();

  const [importXML, importXMLResult] = useImportXmlMutation();
  const [exportXml, exportXmlResult] = useLazyExportXmlQuery();

  useEffect(() => {
    if (createCreditorSepaResult.isSuccess) {
      exportXml({ branchId: id });
    }

    if (createCreditorSepaResult.isError) {
      toasts.danger(
        "Exportación fallida",
        "Ocurrió un error al exportar la contabilidad"
      );
    }
  }, [createCreditorSepaResult]);

  useEffect(() => {
    if (createTransactionResult) {
      if (createTransactionResult.isSuccess) {
        toasts.success(
          "Transacción registrada",
          "La transacción ha sido creada con éxito"
        );

        if (accountings) {
          refetch();
        }
        if (transactions) {
          refetchTransactions();
        }
      }

      if (createTransactionResult.isError) {
        toasts.danger(
          "Cambios no realizados",
          "Ocurrió un error al crear la transacción"
        );
      }
    }
  }, [createTransactionResult]);

  useEffect(() => {
    if (importXMLResult.isSuccess) {
      toasts.success(
        "Importación exitosa",
        "La importación del XML ha sido realizada con éxito"
      );
    }

    if (importXMLResult.isError) {
      toasts.danger(
        "Importación fallida",
        "Ocurrió un error al importar el XML"
      );
    }
  }, [importXMLResult]);

  const handleAccountingExport = () => {
    exportAccounting({ ...filterFormik.values, branchId: id });
  };

  const handleTransactionsExport = () => {
    exportTransactions({ ...filterFormik.values, branchId: id });
  };

  const handleSalesAnalysis = () => {
    exportSalesAnalysis({ ...filterFormik.values, branchId: id });
  };

  const handleCreateTransaction = () => {
    setTransactionFormVisible(true);
  };

  const handleDebitExport = async () => {
    try {
      const response = await createDebit({ branchId: id }).unwrap();
      console.log("handleDebitExport", response);
    } catch (error) {
      const message = error?.data?.error || undefined;
      if (message) {
        toasts.danger("Error al exportar excel", message);
        return;
      }
      toasts.danger("Error al exportar excel", "");
    }
  };

  const handleHistoryExport = () => {
    exportHistory({ ...filterFormik.values, branchId: id });
  };

  const handleDownloadTicket = (transaction: any) => {
    transactionTicket({
      id: transaction.id,
      branchId: id,
    });
  };

  return (
    <Fragment>
      <div className="flex flex-col flex-1 h-full w-full overflow-y-scroll pt-8">
        <div className="flex flex-1 flex-col w-full sm:max-w-full md:max-w-full lg:max-w-full xl:max-w-full 2xl:max-w-full mx-auto mb-8">
          <div className="flex-1 px-4 sm:px-6 lg:px-8">
            <div className="flex sm:flex sm:items-center">
              <div className="flex-grow">
                <h1 className="text-base font-semibold leading-6 text-gray-900">
                  {t("pages.accounting.title")}
                </h1>
                <p className="mt-2 text-sm text-gray-700">
                  {t("pages.accounting.subtitle")}
                </p>
              </div>
              <div className="mt-4 sm:ml-16 sm:mt-0"></div>
            </div>

            <div className="sm:flex sm:items-center sm:justify-center pt-5">
              <div className="flex flex-1 max-w-2xl flex-col sm:flex-row sm:items-center gap-4 sm:space-x-4">
                <ButtonSecondary
                  text="Administrar gastos"
                  onClick={() => {
                    navigate(`/branchs/${id}/accounting/bills`);
                  }}
                  size="extraLarge"
                />
                <ButtonSecondary
                  text={t("accounting.views.agreements")}
                  onClick={() => setAgreementsVisible(true)}
                  size="large"
                />
                {branch && branch.direct_debit_module ? (
                  <ButtonSecondary
                    text={t("accounting.views.manual_direct_debit_xml")}
                    onClick={() => setManualDirectDebitVisible(true)}
                    size="large"
                  />
                ) : null}
              </div>
              <div className="sm:flex w-auto sm:ml-auto">
                <button
                  type="button"
                  className="sm:ml-2 p-2 text-gray-400 hover:text-gray-500"
                  onClick={() => setFilterVisible(!filterVisible)}
                >
                  <span className="sr-only">View KPIs</span>
                  <FontAwesomeIcon
                    icon={faFilter}
                    aria-hidden="true"
                    className="h-6 w-6"
                  />
                </button>
              </div>
            </div>

            <div className="border-b border-gray-300 my-3"></div>
            <div
              className="flex flex-col w-full py-8 gap-4"
              style={{ height: 400 }}
            >
              <div className="flex justify-between">
                <div className="flex flex-col">
                  <h1 className="text-base font-semibold leading-6 text-gray-900">
                    {t("pages.accounting.accountingsTitle")}
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    {t("pages.accounting.accountingsSubtitle")}
                  </p>
                </div>
                <div className="sm:flex w-auto sm:ml-auto">
                  <button
                    type="button"
                    onClick={handleAccountingExport}
                    className="p-2 text-gray-400 hover:text-gray-500 mx-auto"
                  >
                    <span className="sr-only">View KPIs</span>
                    {exportAccountingResult.isLoading ? (
                      <div className="flex justify-center items-center h-7 mt-7">
                        <FontAwesomeIcon
                          icon={faLoader}
                          spin
                          className="text-lg"
                        />
                      </div>
                    ) : (
                      <FontAwesomeIcon
                        icon={faCloudArrowDown}
                        aria-hidden="true"
                        className="h-7 w-7"
                      />
                    )}
                  </button>
                </div>
              </div>
              <AccountingTable
                isEnd={isEnd}
                onLazyLoad={() => setPage((page) => page + 1)}
                accountings={allAccountings || []}
                isLoading={isLoading || isFetching}
                isFetching={isFetching}
              />
            </div>
            <div
              className="flex flex-col w-full py-8 gap-4"
              style={{ height: 500 }}
            >
              <div className="flex flex-col md:flex-row justify-between">
                <div className="flex flex-col">
                  <h1 className="text-base font-semibold leading-6 text-gray-900">
                    {t("pages.accounting.transactionsTitle")}
                  </h1>
                  <p className="mt-2 text-sm text-gray-700">
                    {t("pages.accounting.transactionsSubtitle")}
                  </p>
                </div>
                <div className="flex gap-2 mt-2 items-center">
                  {exportSalesAnalysisResult.isLoading ? (
                    <div className="flex justify-center items-center h-7">
                      <FontAwesomeIcon
                        icon={faLoader}
                        spin
                        className="text-lg"
                      />
                    </div>
                  ) : (
                    <Button
                      text="Analisis de ventas"
                      size="sm"
                      onClick={handleSalesAnalysis}
                    />
                  )}
                  <button
                    type="button"
                    onClick={handleTransactionsExport}
                    className="-m-2 p-2 text-gray-400 hover:text-gray-500"
                  >
                    <span className="sr-only">View KPIs</span>
                    {exportTransactiosResult.isLoading ? (
                      <div className="flex justify-center items-center h-7">
                        <FontAwesomeIcon
                          icon={faLoader}
                          spin
                          className="text-lg"
                        />
                      </div>
                    ) : (
                      <FontAwesomeIcon
                        icon={faCloudArrowDown}
                        aria-hidden="true"
                        className="h-8 w-7"
                      />
                    )}
                  </button>
                  <div>
                    <button
                      onClick={handleCreateTransaction}
                      type="button"
                      className="block rounded-full border-2 border-dashed border-gray-400 p-1 text-gray-400 shadow-sm hover:text-gray-500 focus-visible:outline-none"
                    >
                      <PlusIcon aria-hidden="true" className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
              <TransactionsTable
                isLoading={isLoadingTransactions || isFetchingTransactions}
                transactions={transactions}
                onDelete={(transaction) => {
                  setCurrentTransactionId(transaction.id);
                }}
                onDownloadTicket={handleDownloadTicket}
                onDownloadInvoice={() => {}}
                onNewTransaction={handleCreateTransaction}
                formik={filterFormik}
              />
            </div>
          </div>
        </div>
      </div>

      <AccountingFilter
        branches={branchsOptions}
        visible={filterVisible}
        currencies={currencyOptions}
        personal={personalOptions}
        setVisible={setFilterVisible}
        formik={filterFormik}
      />

      <Formik
        initialValues={transactionInitialValues}
        validationSchema={transactionValidationSchema}
        onSubmit={(values, formikHelpers) => {
          createTransaction({
            ...values,
            branchId: id,
          });
          formikHelpers.resetForm();
          setTransactionFormVisible(false);
        }}
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          values,
          setFieldValue,
          resetForm,
        }) => (
          <DialogCreate
            OnclickCreate={() => {
              handleSubmit();
            }}
            OnclickCancel={() => {
              resetForm();
              setTransactionFormVisible(false);
            }}
            isLoading={createTransactionResult.isLoading}
            tittleButtonCreate={t("general.create")}
            tittleButtonCancel={t("general.cancel")}
            open={transactionFormVisible}
            setOpen={() => setTransactionFormVisible(false)}
            tittle={t("pages.accounting.createTransaction")}
            subTittle={t("pages.accounting.createTransactionDescription")}
          >
            <form action="">
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("pages.accounting.transaction")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.name")}
                          span={t("general.mandatory")}
                          name="name"
                          type="text"
                          placeholder={t("forms.label.name")}
                          onChange={handleChange}
                          error={!!errors.name}
                          textError={errors.name}
                          value={values.name}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("forms.label.description")}
                          span={t("general.mandatory")}
                          name="description"
                          error={!!errors.description}
                          textError={errors.description}
                          onChange={handleChange}
                          value={values.description}
                          type="text"
                          placeholder={t("forms.label.description")}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputDropDownSpan
                          label={t("forms.label.amount")}
                          span={t("general.mandatory")}
                          name="amount"
                          type="number"
                          placeholder={t("forms.label.amount")}
                          onChange={handleChange}
                          value={values.amount}
                          nameSelect="currency"
                          selectValue={values.currency}
                          onSelectChange={handleChange}
                          options={currencies.map((item) => ({
                            ...item,
                            id: item.currency,
                            text: item.currency,
                          }))}
                          error={!!errors.amount}
                          textError={errors.amount}
                          errorSpan={!!errors.currency}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.client")}
                          span={t("general.mandatory")}
                          name="client"
                          error={!!errors.client}
                          textError={errors.client}
                          onChange={handleChange}
                          value={values.client}
                          type="text"
                          placeholder={t("pages.accounting.client")}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.taxId")}
                          span={t("general.mandatory")}
                          name="taxId"
                          error={!!errors.taxId}
                          textError={errors.taxId}
                          onChange={handleChange}
                          value={values.taxId}
                          type="text"
                          placeholder={t("pages.accounting.taxId")}
                        />
                      </div>
                      <div className="col-span-full">
                        <Select
                          name="paymentMethod"
                          span={t("general.mandatory")}
                          selected={paymentMethodOptions.find(
                            (paymentMethod) =>
                              paymentMethod.name == values.paymentMethod
                          )}
                          setSelected={(option) =>
                            setFieldValue("paymentMethod", option.name)
                          }
                          options={paymentMethodOptions}
                          tittle={t("pages.accounting.paymentMethod")}
                          error={!!errors.paymentMethod}
                          textError={errors.paymentMethod}
                        />
                      </div>
                      <div className="col-span-full">
                        <Select
                          name="type"
                          span={t("general.optional")}
                          selected={transactionTypeOptions.find(
                            (type) => type.id == values.type
                          )}
                          setSelected={(option) => {
                            setFieldValue("type", option.id);
                          }}
                          options={transactionTypeOptions}
                          tittle={t("pages.accounting.transactionType")}
                          error={!!errors.type}
                          textError={errors.type}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>
          </DialogCreate>
        )}
      </Formik>

      <Formik
        initialValues={xmlInitialValues}
        validationSchema={xmlValidationSchema}
        onSubmit={(values, formikHelpers) => {
          console.log(values, "Lanzo para crear el XML");
        }}
      >
        {({
          handleChange,
          handleSubmit,
          errors,
          values,
          resetForm,
          validateForm, // Agregar esta función
          setFieldTouched,
        }) => (
          <DialogCreate
            OnclickCreate={() => {
              console.log(values, "Lanzo para crear el XML");
            }}
            OnclickCancel={() => {
              resetForm();
              setManualDirectDebitVisible(false);
            }}
            onClose={() => {
              resetForm();
              setManualDirectDebitVisible(false);
            }}
            isLoading={createTransactionResult.isLoading}
            tittleButtonCreate={t("general.create")}
            tittleButtonCancel={t("general.cancel")}
            open={manualDirectDebitVisible}
            setOpen={() => setManualDirectDebitVisible(false)}
            tittle={t("direct_debit.views.data_sepa")}
            subTittle={""}
            footer={
              <div className="flex justify-end gap-2">
                <Button
                  text="Importar XML"
                  iconRight={faFileUpload}
                  onClick={() => setOpenModalImportXML(!openModalImportXML)}
                />

                <div>
                  {createCreditorSepaResult.isLoading ? (
                    <button
                      className="w-40 flex justify-center items-center bg-white border h-10 rounded cursor-not-allowed"
                      disabled
                    >
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-600 border-t-transparent"></div>
                    </button>
                  ) : (
                    <Button
                      text="Exportar XML"
                      iconRight={faFileDownload}
                      onClick={async () => {
                        // Primero marcar todos los campos como touched
                        const fieldNames = Object.keys(values);
                        await Promise.all(
                          fieldNames.map((fieldName) =>
                            setFieldTouched(fieldName, true, true)
                          )
                        );

                        // Luego validar
                        const formErrors = await validateForm(values);

                        if (Object.keys(formErrors).length === 0) {
                          createCreditorSepa({ branchId: id, ...values });
                        }
                      }}
                    />
                  )}
                </div>

                <div>
                  {createDebitResult.isLoading ||
                  createDebitResult.isFetching ? (
                    <button
                      className="w-40 flex justify-center items-center bg-white border h-10 rounded cursor-not-allowed"
                      disabled
                    >
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-600 border-t-transparent"></div>
                    </button>
                  ) : (
                    <Button
                      text="Exportar Excel"
                      iconRight={faFileExcel}
                      onClick={handleDebitExport}
                    />
                  )}
                </div>

                <Button
                  text="Historial"
                  onClick={() => setHistoryVisible(!historyVisible)}
                />
              </div>
            }
          >
            <form>
              <div className="mt-6 mx-2">
                <div className="space-y-12">
                  <div className="grid grid-cols-1 gap-x-8 gap-y-10 border-b border-gray-900/10 pb-12 md:grid-cols-3">
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <h2 className="text-base font-semibold leading-7 text-gray-900">
                          {t("direct_debit.views.title")}
                        </h2>
                      </div>
                    </div>
                    <div className="grid max-w-2xl grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-6 md:col-span-2">
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.forms.creditor")}
                          span={t("general.mandatory")}
                          name="creditorName"
                          type="text"
                          placeholder={""}
                          onChange={handleChange}
                          error={!!errors.creditorName}
                          textError={errors.creditorName}
                          value={values.creditorName}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.forms.iban")}
                          span={t("general.mandatory")}
                          name="creditorIban"
                          error={!!errors.creditorIban}
                          textError={errors.creditorIban}
                          onChange={handleChange}
                          value={values.creditorIban}
                          type="text"
                          placeholder={"FI1350001540000056"}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.forms.creditorAgent")}
                          span={t("general.mandatory")}
                          name="creditorAgent"
                          error={!!errors.creditorAgent}
                          textError={errors.creditorAgent}
                          onChange={handleChange}
                          value={values.creditorAgent}
                          type="text"
                          placeholder={"PSSTFRPPMON"}
                        />
                      </div>
                      <div className="col-span-full">
                        <InputWithCornerHint
                          label={t("pages.accounting.forms.creditor_id")}
                          span={t("general.mandatory")}
                          name="creditorId"
                          error={!!errors.creditorId}
                          textError={errors.creditorId}
                          onChange={handleChange}
                          value={values.creditorId}
                          type="text"
                          placeholder={"DE21WVM1234567890"}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <button type="submit" className="hidden"></button>
            </form>
          </DialogCreate>
        )}
      </Formik>

      {/* MODAL FOR CONVENIOS */}
      <ModalXXL
        open={agreementsVisible}
        Close={() => setAgreementsVisible(false)}
        tittleModal={t("accounting.views.agreements")}
        //textButtonCreate={t("general.create")}
        textButtonClose={t("general.cancel")}
        modalClassName="z-50 w-[700px]"
      >
        <div className="flex flex-col gap-4">
          <div className="flex ">
            <DateRangePicker
              id="agreements-date"
              label={t("lesson.views.interval_date")}
              value={{
                startDate: agreementsFormik.values.startDate
                  ? DateTime.fromFormat(
                      agreementsFormik.values.startDate,
                      "yyyy-MM-dd"
                    ).toJSDate()
                  : new Date(),
                endDate: agreementsFormik.values.endDate
                  ? DateTime.fromFormat(
                      agreementsFormik.values.endDate,
                      "yyyy-MM-dd"
                    ).toJSDate()
                  : new Date(),
              }}
              onDateChange={(startDate, endDate) => {
                agreementsFormik.setFieldValue(
                  "startDate",
                  DateTime.fromJSDate(startDate).toFormat("yyyy-MM-dd")
                );
                agreementsFormik.setFieldValue(
                  "endDate",
                  DateTime.fromJSDate(endDate).toFormat("yyyy-MM-dd")
                );
              }}
            />
          </div>
          <AgreementsTable
            agreements={agreementsQuery.data ?? []}
            isLoading={agreementsQuery.isLoading}
            onDownloadTicket={(agreement) => {
              setIdTicket(agreement.id);
              setAgreementsSummaryVisible(true);
              setAgreementsVisible(false);
            }}
          />
        </div>
      </ModalXXL>

      {/* MODAL FOR TICKET OF CONVENIOS */}
      <Modal
        open={agreementsSummaryVisible}
        Close={() => setAgreementsSummaryVisible(false)}
        tittleModal={t("accounting.views.company_agreements")}
        textButtonClose={t("general.cancel")}
        modalClassName="z-50"
        modalWidth="sm:max-w-3xl"
        footer={
          <div className="flex justify-end gap-2">
            <Button
              text={"Exportar"}
              onClick={() => {
                window.open(
                  `${DOMAIN}/admin/company-agreements-summary-export?fechaInicio=${agreementsFormik.values.startDate}&fechaFin=${agreementsFormik.values.endDate}&tag=${idTicket}`,
                  "_blank"
                );
              }}
            />

            <Button
              text={t("general.cancel")}
              onClick={() => {
                setAgreementsSummaryVisible(false);
                setAgreementsVisible(true);
              }}
            />
            <Button
              text={"Confirmar montos pagados"}
              className="bg-primary"
              onClick={() => {
                setConfirmSummaryVisible(true);
                setAgreementsSummaryVisible(false);
              }}
            />
          </div>
        }
      >
        <div className="flex flex-col gap-4">
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
            <div className="flex">
              <div className="flex-shrink-0">
                <TriangleAlert className="text-yellow-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-yellow-700">
                  <strong>Importante:</strong> Confirmo que todos estos montos
                  han sido pagados de manera externa a esta plataforma
                </p>
              </div>
            </div>
          </div>
          <AgreementsSummaryTable
            agreements={agreementsSummaryQuery.data ?? []}
            isLoading={agreementsSummaryQuery.isLoading}
          />
        </div>
      </Modal>

      {/* MODAL FOR CONFIRMATION OF CONVENIOS */}
      <Formik
        enableReinitialize
        validationSchema={Yup.object({
          date: Yup.string().required("Este campo es obligatorio"),
        })}
        initialValues={{ date: "" }}
        onSubmit={(values) => {
          console.log(values, "Lanzo para confirmar");
          setConfirmSummaryVisible(false);
          setAgreementsSummaryVisible(false);
        }}
      >
        {({
          handleChange,
          setFieldValue,
          values,
          handleSubmit,
          resetForm,
          errors,
        }) => (
          <Modal
            open={confirmSummaryVisible}
            Close={() => {
              resetForm();
              setConfirmSummaryVisible(false);
            }}
            onClickCreate={handleSubmit}
            tittleModal={t("accounting.views.confirm_summary")}
            textButtonCreate={t("user.views.confirm")}
            textButtonClose={t("general.cancel")}
            onClose={() => {
              resetForm();
              setConfirmSummaryVisible(false);
            }}
            modalClassName="z-50"
          >
            <div className="flex flex-col gap-4">
              <div className="col-span-full">
                <InputWithCornerHint
                  label={t("accounting.views.messsage_confirm_summary")}
                  span={t("general.mandatory")}
                  name="date"
                  error={!!errors.date}
                  textError={errors.date}
                  onChange={handleChange}
                  value={values.date}
                  type="date"
                  placeholder={t("pages.accounting.client")}
                />
              </div>
            </div>
          </Modal>
        )}
      </Formik>

      {/* IMPORTAR XML */}
      <Formik
        enableReinitialize
        initialValues={currentFee ?? xmlImportInitialValues}
        validationSchema={xmlImportValidationSchema}
        onSubmit={(values: XMLImportValues, formikHelpers) => {
          console.log(values, "Lanzo para crear el XML");
        }}
      >
        {({
          handleChange,
          setFieldValue,
          values,
          handleSubmit,
          resetForm,
          errors,
        }) => (
          <Modal
            open={openModalImportXML}
            Close={() => {
              setOpenModalImportXML(!openModalImportXML);
              resetForm();
            }}
            onClickCreate={() => {
              if (currentFee) {
                //updateFee({ ...values, branchId: id, id: currentFee.id });
              } else {
                importXML({ ...values, branchId: id });
              }

              setCurrentFee(undefined);
              setOpenModalImportXML(!openModalImportXML);
              resetForm();
            }}
            textButtonCreate={t("training-wizard.views.continue")}
            textButtonClose={t("general.cancel")}
            tittleModal={t("direct_debit.views.import_xml")}
          >
            <form onSubmit={handleSubmit}>
              <div className="gap-4">
                <div className="col-span-full mt-5">
                  <p className="text-sm text-gray-500 px-4">
                    {t("direct_debit.views.q_datetime")}
                  </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-5">
                  <div className="col-span-full md:col-span-1 px-4">
                    <InputWithCornerHint
                      label={t("accounting.views.date")}
                      name="date"
                      type="date"
                      placeholder={t("forms.label.date")}
                      onChange={handleChange}
                      error={!!errors.date}
                      textError={errors.date}
                      value={values.date}
                    />
                  </div>
                  <div className="col-span-full md:col-span-1 px-4">
                    <InputWithCornerHint
                      label={t("accounting.views.time")}
                      name="time"
                      type="time"
                      placeholder={t("accounting.views.time")}
                      onChange={handleChange}
                      error={!!errors.time}
                      textError={errors.time}
                      value={values.time}
                    />
                  </div>
                </div>

                <div className="col-span-full mt-5">
                  <p className="text-sm text-gray-500 px-4">
                    {t("direct_debit.views.import_message")}
                  </p>
                </div>
                <div className="col-span-full md:col-span-1 px-4 mt-3">
                  <InputFileXML
                    name="file"
                    id="file"
                    label={""}
                    span={""}
                    spanButton={t("general.up_archive")}
                    description={t("accounting.views.format_xml")}
                    onFileChange={(file) => {
                      setFieldValue("file", file);
                    }}
                    onRemoveFile={() => {
                      setFieldValue("file", null);
                    }}
                    tittleDelete={t("gym.views.delete_image")}
                  />
                </div>
              </div>
            </form>
          </Modal>
        )}
      </Formik>

      {/* MODAL FOR HISTORY */}
      <ModalXXL
        open={historyVisible}
        Close={() => setHistoryVisible(false)}
        tittleModal={t("pages.accounting.historyTitle")}
        textButtonClose={t("general.cancel")}
        modalClassName="z-50"
      >
        <div className="flex flex-col gap-4">
          <div className="flex ">
            <div className="flex w-full justify-end">
              <button
                type="button"
                onClick={handleHistoryExport}
                className="-m-2 p-2 text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">View KPIs</span>
                <FontAwesomeIcon
                  icon={faCloudArrowDown}
                  aria-hidden="true"
                  className="h-7 w-7"
                />
              </button>
            </div>
          </div>
          <HistoryTable
            agreements={failedHistoryQuery.data ?? []}
            isLoading={failedHistoryQuery.isLoading}
            onDownloadTicket={() => {}}
          />
        </div>
      </ModalXXL>

      <AlertErrorButtons
        onClose={() => setCurrentTransactionId(null)}
        open={!!currentTransactionId}
        title="Contabilidad"
        message="¿Confirmas que quieres eliminar este registro? Ten en cuenta que esta acción no puede deshacerse."
        onPrimaryButtonClick={async () => {
          try {
            await deleteTransaction({
              branchId: id,
              id: currentTransactionId,
            }).unwrap();

            toasts.success(
              "Transacción eliminada",
              "La transacción ha sido eliminada con éxito."
            );
            setCurrentTransactionId(null);
            if (accountings) {
              refetch();
            }
            if (transactions) {
              refetchTransactions();
            }
          } catch (error) {
            console.log({ error });
            toasts.danger(
              "Error contabilidad",
              "Ha ocurrido un error al momento de eliminar este registro. Porfavor intenta más tarde"
            );
          }
        }}
        onSecondaryButtonClick={() => setCurrentTransactionId(null)}
        primaryButtonText="Confirmar"
        secondaryButtonText="Cancelar"
        modalClassName="z-50"
        isLoading={deleteTransactionResult.isLoading}
      />
    </Fragment>
  );
};
